/**
 * Duelos asíncronos.
 *
 * Asíncrono de verdad: los dos jugadores nunca coinciden. Se enfrenta el MAZO
 * del rival, jugado por la misma IA local, y el servidor vuelve a simular la
 * partida con la semilla y las jugadas para decidir quién ganó.
 *
 * El cliente nunca dice quién ganó. Envía lo que hizo; el resultado lo calcula
 * el servidor ejecutando el mismo motor.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/services/queryClient';
import { describeDatabaseError, requireSupabase, supabase } from '@/services/supabaseClient';
import type { DeckEntry } from '@/engine/combatEngine';
import type { PvpOpponent, RecordedMove } from '@/store/useGameStore';

export interface MatchOutcome {
  matchId: string;
  result: 'PLAYER' | 'OPPONENT' | 'DRAW';
  reason: string;
  zonesWon: { PLAYER: number; OPPONENT: number };
  totalPower: { PLAYER: number; OPPONENT: number };
  goldAwarded: number;
  /** El pago se recortó por el tope diario. */
  goldCapped: boolean;
  ratingChange: number;
}

/** Publica el mazo propio en el tablón para que otros lo enfrenten. */
export function usePublishDeck(userId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await requireSupabase().rpc('publish_pvp_deck', {});
      if (error) throw new Error(describeDatabaseError(error));
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pvp', 'me', userId] });
    },
  });
}

/** Estado propio en el tablón: si está publicado y con qué puntuación. */
export function useMyPvpStatus(userId: string | undefined) {
  return useQuery({
    queryKey: ['pvp', 'me', userId] as const,
    enabled: Boolean(userId && supabase),
    queryFn: async () => {
      const { data, error } = await supabase!
        .from('pvp_decks')
        .select('*')
        .eq('user_id', userId!)
        .maybeSingle();
      if (error) throw new Error(describeDatabaseError(error));
      return data;
    },
  });
}

/** Historial de duelos propios. */
export function usePvpHistory(userId: string | undefined) {
  return useQuery({
    queryKey: ['pvp', 'history', userId] as const,
    enabled: Boolean(userId && supabase),
    queryFn: async () => {
      const { data, error } = await supabase!
        .from('pvp_matches')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) throw new Error(describeDatabaseError(error));
      return data;
    },
  });
}

export function useFindOpponent() {
  return useMutation({
    mutationFn: async (): Promise<PvpOpponent> => {
      const { data, error } = await requireSupabase().rpc('find_pvp_opponent', {});
      if (error) throw new Error(describeDatabaseError(error));
      const row = data as { user_id: string; username: string; deck: unknown };
      return {
        userId: row.user_id,
        username: row.username,
        deck: row.deck as DeckEntry[],
      };
    },
  });
}

/**
 * Envía la partida terminada al servidor para que la valide.
 *
 * Se llama a la Edge Function con `fetch` y no por `supabase.functions.invoke`
 * para que el error del servidor llegue con su mensaje: cuando una partida se
 * rechaza por jugada ilegal, saber cuál era es la diferencia entre arreglar un
 * fallo y adivinar.
 */
export function useSubmitMatch(userId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      defenderId: string;
      seed: number;
      moves: RecordedMove[];
    }): Promise<MatchOutcome> => {
      const client = requireSupabase();
      const { data: session } = await client.auth.getSession();
      const token = session.session?.access_token;
      if (!token) throw new Error('Tu sesión ha caducado. Vuelve a entrar.');

      const baseUrl = (client as unknown as { supabaseUrl: string }).supabaseUrl;
      const response = await fetch(`${baseUrl}/functions/v1/submit-match`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });

      const payload = (await response.json()) as MatchOutcome & { error?: string };
      if (!response.ok) throw new Error(payload.error ?? 'El servidor rechazó la partida');
      return payload;
    },
    // Sin reintento: un reenvío a ciegas registraría la partida dos veces.
    retry: false,
    onSuccess: () => {
      if (!userId) return;
      void queryClient.invalidateQueries({ queryKey: queryKeys.profile(userId) });
      void queryClient.invalidateQueries({ queryKey: ['pvp', 'history', userId] });
      void queryClient.invalidateQueries({ queryKey: ['pvp', 'me', userId] });
    },
  });
}
